import {
  ConfirmationMessageTypeEnum,
  isTerminalToolName,
} from '@terra/shared-dto-chat-types';
import type { ProcessedPart } from '../utils/message-processing';

const OBJECT_RESULT_TOOL_NAMES = new Set([
  'finding-tool',
  'create-finding',
  'create-findings',
  'create_finding',
  'create_findings',
  'update-finding',
  'update-findings',
  'update_finding',
  'update_findings',
  'update-finding-description',
  'update_finding_description',
  'signal-tool',
  'update-signal-tool',
  'create-signal',
  'create-signals',
  'create_signal',
  'create_signals',
  'update-signal',
  'update-signals',
  'update_signal',
  'update_signals',
]);

const READ_PATH_KEYS = [
  'filePath',
  'file_path',
  'targetFile',
  'target_file',
  'filename',
  'path',
] as const;

function getDataMessageRecord(
  processedPart: ProcessedPart,
): Record<string, unknown> | undefined {
  const dataMessage = processedPart.dataMessage;

  if (!dataMessage || typeof dataMessage !== 'object') {
    return undefined;
  }

  return dataMessage as Record<string, unknown>;
}

function getProviderMetadataRecord(
  processedPart: ProcessedPart,
): Record<string, unknown> | undefined {
  const part = processedPart.part as
    | { providerMetadata?: { metadata?: unknown } }
    | undefined;
  const metadata = part?.providerMetadata?.metadata;

  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }

  return metadata as Record<string, unknown>;
}

export function getToolNameFromPart(
  processedPart: ProcessedPart,
): string | undefined {
  const dataMessageRecord = getDataMessageRecord(processedPart);

  if (typeof dataMessageRecord?.toolName === 'string') {
    return dataMessageRecord.toolName;
  }

  return undefined;
}

export function getToolStatusFromPart(
  processedPart: ProcessedPart,
): string | undefined {
  const dataMessageRecord = getDataMessageRecord(processedPart);

  if (typeof dataMessageRecord?.status === 'string') {
    return dataMessageRecord.status;
  }

  return undefined;
}

export function getToolCallIdFromPart(
  processedPart: ProcessedPart,
): string | undefined {
  const dataMessageRecord = getDataMessageRecord(processedPart);

  if (typeof dataMessageRecord?.toolCallId === 'string') {
    return dataMessageRecord.toolCallId;
  }

  return undefined;
}

export function getParentToolCallIdFromPart(
  processedPart: ProcessedPart,
): string | undefined {
  const dataMessageRecord = getDataMessageRecord(processedPart);
  const metadata = dataMessageRecord?.metadata;
  const dataMessageMetadata =
    metadata && typeof metadata === 'object'
      ? (metadata as Record<string, unknown>)
      : undefined;

  if (typeof dataMessageMetadata?.parentToolCallId === 'string') {
    return dataMessageMetadata.parentToolCallId;
  }

  const toolCallId = getToolCallIdFromPart(processedPart);
  const nestedChildMatch = toolCallId?.match(/^(.*):child:/);

  if (nestedChildMatch?.[1]) {
    return nestedChildMatch[1];
  }

  const providerMetadata = getProviderMetadataRecord(processedPart);

  if (typeof providerMetadata?.parentToolCallId === 'string') {
    return providerMetadata.parentToolCallId;
  }

  return undefined;
}

export function getToolPayloadFromPart(processedPart: ProcessedPart): unknown {
  if (processedPart.kind !== 'data-message' || !processedPart.dataMessage) {
    return undefined;
  }

  const dataMessageRecord = processedPart.dataMessage as {
    input?: unknown;
    output?: unknown;
  };

  return dataMessageRecord.input ?? dataMessageRecord.output;
}

export function getToolOutputFromPart(processedPart: ProcessedPart): unknown {
  if (processedPart.kind !== 'data-message' || !processedPart.dataMessage) {
    return undefined;
  }

  const dataMessageRecord = processedPart.dataMessage as {
    output?: unknown;
  };

  return dataMessageRecord.output;
}

export function hasVisibleToolOutput(processedPart: ProcessedPart): boolean {
  const output = getToolOutputFromPart(processedPart);

  if (typeof output === 'string') {
    return output.trim().length > 0;
  }

  return output !== undefined && output !== null;
}

export type AnalyzerProgressEvent = {
  id?: string;
  stage: string;
  detail?: string;
  timestamp?: number;
};

export function getProgressEventsFromPart(
  processedPart: ProcessedPart,
): AnalyzerProgressEvent[] {
  if (processedPart.kind !== 'data-message' || !processedPart.dataMessage) {
    return [];
  }

  const dataMessageRecord = processedPart.dataMessage as {
    progressEvents?: unknown;
  };
  const events = dataMessageRecord.progressEvents;

  if (!Array.isArray(events)) {
    return [];
  }

  return events.flatMap((event): AnalyzerProgressEvent[] => {
    if (!event || typeof event !== 'object') {
      return [];
    }

    const record = event as {
      id?: unknown;
      stage?: unknown;
      detail?: unknown;
      timestamp?: unknown;
    };

    if (typeof record.stage !== 'string') {
      return [];
    }

    return [
      {
        stage: record.stage,
        ...(typeof record.id === 'string' ? { id: record.id } : {}),
        ...(typeof record.detail === 'string' ? { detail: record.detail } : {}),
        ...(typeof record.timestamp === 'number'
          ? { timestamp: record.timestamp }
          : {}),
      },
    ];
  });
}

export function isOmittedNestedAnalyzerPart(
  processedPart: ProcessedPart,
): boolean {
  return (
    isCodeAnalyzerToolPart(processedPart) &&
    !hasVisibleToolOutput(processedPart)
  );
}

export function isCodeAnalyzerToolPart(processedPart: ProcessedPart): boolean {
  const toolName = getToolNameFromPart(processedPart);

  if (!toolName) {
    return false;
  }

  const normalized = toolName.replace(/_/g, '-').toLowerCase();

  return normalized === 'code-analyzer-tool' || normalized === 'code-analyzer';
}

export function getReadPathFromPayload(payload: unknown): string {
  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const payloadRecord = payload as Record<string, unknown>;

  for (const key of READ_PATH_KEYS) {
    const value = payloadRecord[key];

    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return '';
}

export function isRunningPart(processedPart: ProcessedPart): boolean {
  const status = getToolStatusFromPart(processedPart);

  return status === 'started' || status === 'in-progress';
}

export function isPendingHITLPart(processedPart: ProcessedPart): boolean {
  if (processedPart.kind !== 'data-message') {
    return false;
  }

  const dataMessage = processedPart.dataMessage;
  const dataMessageRecord = dataMessage as { decision?: unknown } | undefined;

  return (
    dataMessage?.type === ConfirmationMessageTypeEnum.HITLRequestConfirmation &&
    !processedPart.selectedDecision &&
    !processedPart.hitlDecision &&
    !dataMessageRecord?.decision
  );
}

export function isObjectResultPart(processedPart: ProcessedPart): boolean {
  const toolName = getToolNameFromPart(processedPart);
  const status = getToolStatusFromPart(processedPart);

  return Boolean(
    toolName &&
      OBJECT_RESULT_TOOL_NAMES.has(toolName.replace(/_/g, '-').toLowerCase()) &&
      status === 'completed',
  );
}

export function isCompletedTerminalResultPart(
  processedPart: ProcessedPart,
): boolean {
  return (
    isTerminalToolName(getToolNameFromPart(processedPart)) &&
    getToolStatusFromPart(processedPart) === 'completed' &&
    hasVisibleToolOutput(processedPart)
  );
}
